import { IconButton } from "@fluentui/react/lib/Button";
import { IStackTokens, Stack } from "@fluentui/react/lib/Stack";
import React from "react";
import { CustomMultiItemCarouselProps } from "../Interfaces/CustomMultiItemCarousel.interfaces";
import { useResponsiveVisibleCount } from "./useResponsiveVisibleCount";

export function CustomMultiItemCarousel<T>({
  gap = 16,
  items,
  onStartChange,
  renderItem,
  visibleCount = 3
}: Readonly<CustomMultiItemCarouselProps<T>>) {
  const currentVisibleCount = useResponsiveVisibleCount(visibleCount);
  const [start, setStart] = React.useState(0);
  const stackTokens: IStackTokens = { childrenGap: gap };
  const canGoBack = start > 0;
  const canGoForward = start + currentVisibleCount < items.length;
  const visibleItems = React.useMemo(
    () => items.slice(start, start + currentVisibleCount),
    [items, start, currentVisibleCount]
  );

  const handleBack = React.useCallback(
    () => setStart(Math.max(0, start - currentVisibleCount)),
    [start, currentVisibleCount]
  );
  const handleForward = React.useCallback(
    () => setStart(Math.min(items.length - currentVisibleCount, start + currentVisibleCount)),
    [items.length, currentVisibleCount, start]
  );

  // Reset start to 0 when visible count changes
  React.useEffect(() => {
    onStartChange?.(start);
  }, [start, onStartChange]);

  return (
    <>
      <Stack
        horizontal
        horizontalAlign="end"
        tokens={{ childrenGap: 5 }}
        verticalAlign="center"
        styles={{ root: { marginBottom: 8 } }}
      >
        <IconButton
          iconProps={{ iconName: "ChevronLeft" }}
          disabled={!canGoBack}
          onClick={handleBack}
          ariaLabel="Previous"
          title="Previous"
          styles={{
            root: { width: 28, height: 28, fontSize: 14, borderRadius: 5 }
          }}
        />
        <IconButton
          ariaLabel="Next"
          disabled={!canGoForward}
          iconProps={{ iconName: "ChevronRight" }}
          onClick={handleForward}
          styles={{
            root: { width: 28, height: 28, fontSize: 14, borderRadius: 5 }
          }}
          title="Next"
        />
      </Stack>
      <Stack horizontal tokens={stackTokens} styles={{ root: { overflow: "hidden" } }}>
        {visibleItems.map((item, idx) => (
          <div style={{ flex: 1, minWidth: 0 }} key={start + idx}>
            {renderItem(item, start + idx)}
          </div>
        ))}
      </Stack>
    </>
  );
}
